import { ProjectFileType } from '@/generated/graphql';
import { formatDate, formatFileSize } from '@/common/utils/fileUtils';
import {
  Box,
  Typography,
  Button,
  Divider,
  Chip,
  Skeleton,
} from '@mui/material';
import React from 'react';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  FolderZip as FolderZipIcon,
  FolderOpen as FolderOpenIcon,
  RestartAlt as ResetLocationIcon,
} from '@mui/icons-material';
import { ProjectEditForm } from '../components/ProjectEditForm';
import { useProjectOperations } from '../hooks/useProjectOperations';
import { useProjectDeletion } from '../hooks/useProjectDeletion';
import { useProjectDetails } from '../context/ProjectDetailsContext';
import { useProjectPermissions } from '@/hooks/projectPermissions';

interface ProjectOverviewTabProps {
  onClose: () => void;
}

const ProjectOverviewTab: React.FC<ProjectOverviewTabProps> = ({ onClose }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const {
    project,
    projectId,
    latestVersionFiles,
    latestVersionLoading,
    latestVersionError,
    refetchProject,
    syncProjectDir,
    handleChangeProjectLocation,
    handleResetProjectLocation,
  } = useProjectDetails();

  const { canDelete, canEdit } = useProjectPermissions(project);

  // Use project operations hook
  const { handleDownloadProject, isDownloading } = useProjectOperations({
    project: project ?? null,
    latestVersionFiles,
  });

  // Use project deletion hook
  const { handleDeleteProject, deleteLoading } = useProjectDeletion({
    projectId,
    onClose,
  });

  const handleResetLocation = () => {
    const confirmed = window.confirm(
      'Reset stored project location for this project? You can set a new location at any time.'
    );
    if (!confirmed) {
      return;
    }

    handleResetProjectLocation();
  };

  return (
    <Box
      sx={{
        display: 'grid',
        flexDirection: { xs: 'column', md: 'row' },
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          lg: 'minmax(0, 1.2fr) minmax(0, 1fr)',
        },
        gap: 4,
        '& p': { overflowWrap: 'anywhere' },
      }}
    >
      <Box flex={1} sx={{ minWidth: 0 }}>
        {isEditing ? (
          <ProjectEditForm
            projectId={projectId}
            initialValues={{
              name: project?.name || '',
              description: project?.description || '',
              tags: project?.tags || [],
            }}
            onCancel={() => setIsEditing(false)}
            onSuccess={() => {
              setIsEditing(false);
              refetchProject();
            }}
          />
        ) : (
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
              mb={3}
            >
              <Typography variant="h6">Project Settings</Typography>
              {canEdit && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditing(true)}
                  name="editProject"
                >
                  Edit Project
                </Button>
              )}
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom>
                Project Name
              </Typography>
              <Typography variant="body1">{project?.name}</Typography>
            </Box>

            {project?.description && (
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1">{project.description}</Typography>
              </Box>
            )}

            {syncProjectDir && <Divider sx={{ my: 3 }} />}

            {syncProjectDir || canDelete ? (
              <Box display="flex" gap={1} flexWrap="wrap">
                {syncProjectDir ? (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<FolderOpenIcon />}
                      onClick={handleChangeProjectLocation}
                    >
                      Change Location
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<ResetLocationIcon />}
                      onClick={handleResetLocation}
                    >
                      Reset Location
                    </Button>
                  </>
                ) : null}
              </Box>
            ) : null}
          </Box>
        )}
      </Box>

      <Box
        flex={1}
        sx={{
          minWidth: 0,
          borderLeft: { lg: '1px solid' },
          borderTop: { xs: '1px solid', lg: 0 },
          borderColor: { xs: 'divider', lg: 'divider' },
          pl: { lg: 4 },
          pt: { xs: 4, lg: 0 },
        }}
      >
        <Typography variant="h6" gutterBottom>
          Project Info
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Created: {formatDate(project?.createdAt || '-')}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Updated: {formatDate(project?.updatedAt || '-')}
        </Typography>

        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Latest version files
          </Typography>
          {latestVersionLoading ? (
            <Box role="status" aria-label="Loading file statistics">
              <Skeleton width="65%" />
              <Skeleton width="50%" />
            </Box>
          ) : latestVersionError ? (
            <Typography variant="body2" color="text.secondary">
              File statistics unavailable.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" gutterBottom>
                Total Files: {latestVersionFiles?.length ?? '-'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Total Size:{' '}
                {formatFileSize(
                  latestVersionFiles?.reduce(
                    (total: number, file: ProjectFileType) => total + file.size,
                    0
                  ) || 0
                )}
              </Typography>
            </>
          )}

          {/* Download Project Button */}
          <Box mt={3}>
            <Button
              variant="contained"
              startIcon={<FolderZipIcon />}
              onClick={handleDownloadProject}
              disabled={
                !latestVersionFiles ||
                latestVersionFiles.length === 0 ||
                isDownloading ||
                latestVersionLoading ||
                !!latestVersionError
              }
              fullWidth
            >
              Download Project Folder
            </Button>
            <Typography
              variant="caption"
              display="block"
              mt={1}
              color="text.secondary"
              textAlign="center"
            >
              {'Downloads the latest version to a new location'}
            </Typography>
          </Box>

          {project?.tags && project?.tags.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {project.tags.map((tag: string) => (
                  <Chip key={tag} label={tag} size="small" />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {canDelete && !isEditing && (
        <Box
          sx={{
            gridColumn: '1 / -1',
            borderTop: 1,
            borderColor: 'divider',
            pt: 3,
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2">Delete project</Typography>
            <Typography variant="body2" color="text.secondary">
              Permanently delete this project and its files.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteProject}
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Project'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProjectOverviewTab;
